import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const DemoPage = () => {
  return (
    <>
      <SEO
        title="Démo technique - Tunisie Parking | Tester les applications"
        description="Guide complet pour installer et tester les applications Tunisie Parking. Instructions pour Expo Go, liens de démo pour l'application mobile, contrôleur et Web admin."
        keywords="démo tunisie parking, tester parking tunisie, expo go parking, démo application parking, test parking tunisie"
        url="https://parking.tn/demo"
      />
      <div className="min-h-screen bg-gray-50">
      {/* Header avec navigation */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-bold mb-4">Démo technique</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Guide complet pour installer et tester les applications Tunisie Parking
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Installation d'Expo Go */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">📱 Installation d'Expo Go</h2>
          
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <p className="text-gray-700 leading-relaxed mb-4">
              Pour tester les applications mobiles (App Mobile et App Contrôleur), vous devez d'abord installer <strong>Expo Go</strong> sur votre smartphone.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Expo Go est une application qui permet de tester des applications React Native développées avec Expo sans avoir à les compiler.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <a
                href="https://apps.apple.com/app/expo-go/id982107779"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center px-6 py-4 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C1.79 15.25 2.28 7.59 9.5 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Télécharger sur l'App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=host.exp.exponent"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.19,15.12L14.54,12.85L17.19,10.81L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                Télécharger sur Google Play
              </a>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <h4 className="text-lg font-semibold text-blue-900 mb-2">📋 Étapes d'installation</h4>
              <ol className="list-decimal list-inside text-blue-800 space-y-2">
                <li>Téléchargez Expo Go depuis l'App Store (iOS) ou Google Play (Android)</li>
                <li>Ouvrez l'application Expo Go sur votre smartphone</li>
                <li>Assurez-vous que votre téléphone est connecté à Internet</li>
                <li>Cliquez sur les liens de démo ci-dessous pour ouvrir les applications</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Accès aux Applications de Démo */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">🚀 Accès aux applications de démo</h2>
          
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            {/* App Mobile */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">📱</span>
                <h3 className="text-2xl font-semibold text-gray-900">Application mobile</h3>
              </div>
              <p className="text-gray-700 mb-4">
                <strong>Lien de démo :</strong>{' '}
                <a 
                  href="exp://mobile.parking.tn:8081" 
                  className="text-primary-600 hover:text-primary-700 underline break-all font-mono"
                >
                  exp://mobile.parking.tn:8081
                </a>
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                <p className="text-gray-700 mb-2">
                  <strong>Comptes de démo disponibles :</strong>
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Vous pouvez créer un nouveau compte avec n'importe quel numéro de téléphone tunisien (format : +216XXXXXXXX)</li>
                  <li>Ou utiliser le compte de démo existant avec des données de test : <span className="font-mono font-semibold">+21698123456</span></li>
                </ul>
                <p className="text-gray-700 mt-3 text-sm">
                  <strong>Note :</strong> Le compte de démo contient des données de test (tickets, transactions, véhicules) pour vous permettre de tester toutes les fonctionnalités sans avoir à créer un nouveau compte.
                </p>
              </div>
            </div>

            {/* App Contrôleur */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">🚗</span>
                <h3 className="text-2xl font-semibold text-gray-900">Application contrôleur</h3>
              </div>
              <p className="text-gray-700 mb-4">
                <strong>Lien de démo :</strong>{' '}
                <a 
                  href="exp://controller.parking.tn:8082" 
                  className="text-primary-600 hover:text-primary-700 underline break-all font-mono"
                >
                  exp://controller.parking.tn:8082
                </a>
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                <p className="text-gray-700 mb-2">
                  <strong>Compte de démo :</strong>
                </p>
                <p className="text-gray-700 mb-2">
                  Pour vous connecter en tant que contrôleur, utilisez le numéro de téléphone suivant :
                </p>
                <p className="text-lg font-mono font-semibold text-gray-900 bg-white px-4 py-2 rounded border border-green-300 inline-block">
                  +21698123460
                </p>
                <p className="text-gray-700 mt-3 text-sm">
                  <strong>Note :</strong> Ce compte a le rôle CONTROLLER et vous permettra de tester toutes les fonctionnalités de contrôle (scan de plaques, vérification de tickets, historique).
                </p>
              </div>
            </div>

            {/* Web Admin */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">💼</span>
                <h3 className="text-2xl font-semibold text-gray-900">Web admin</h3>
              </div>
              <p className="text-gray-700 mb-4">
                <strong>Lien de démo :</strong>{' '}
                <a 
                  href="https://admin.parking.tn" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline font-mono"
                >
                  https://admin.parking.tn
                </a>
              </p>
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                <p className="text-gray-700 mb-2">
                  <strong>Compte de démo :</strong>
                </p>
                <p className="text-gray-700 mb-2">
                  Pour vous connecter en tant qu'administrateur, utilisez le numéro de téléphone suivant :
                </p>
                <p className="text-lg font-mono font-semibold text-gray-900 bg-white px-4 py-2 rounded border border-purple-300 inline-block">
                  +21620123456
                </p>
                <p className="text-gray-700 mt-3 text-sm">
                  <strong>Note :</strong> Ce compte a le rôle ADMIN et vous donnera accès à toutes les fonctionnalités d'administration (dashboard, gestion des parkings, utilisateurs, tickets, rapports).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Informations importantes */}
        <section className="mb-16">
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
            <h4 className="text-lg font-semibold text-yellow-900 mb-2">⚠️ Important</h4>
            <ul className="list-disc list-inside text-yellow-800 space-y-1">
              <li>Assurez-vous d'avoir installé Expo Go avant d'essayer d'ouvrir les liens des applications mobiles</li>
              <li>Les applications mobiles nécessitent une connexion Internet active</li>
              <li>Les comptes de démo sont destinés uniquement à des fins de test et de démonstration</li>
              <li>Si vous rencontrez des problèmes, n'hésitez pas à nous contacter</li>
            </ul>
          </div>
        </section>

        {/* Section Contact */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-primary-600 to-primary-700 text-white rounded-xl shadow-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Besoin d'aide ?</h2>
            <p className="text-xl text-primary-100 mb-6">
              Si vous rencontrez des difficultés lors de l'installation ou de l'utilisation des applications de démo, 
              notre équipe est là pour vous aider.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-6 py-3 bg-white text-primary-700 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
              >
                Page de Contact
              </Link>
              <a
                href="mailto:hello@buildmybrand.art"
                className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-400 transition-colors"
              >
                Email support
              </a>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
    </>
  );
};

export default DemoPage;

